import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Globe, Server, Cloud, Smartphone, Code2, ArrowUpRight, Github, ExternalLink, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { projects } from '../../data/projects'

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedProject, setSelectedProject] = useState<number | null>(null)

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'web', name: 'Web' },
    { id: 'backend', name: 'Backend' },
    { id: 'devops', name: 'DevOps' }
  ]

  const filteredProjects = selectedCategory && selectedCategory !== 'all'
    ? projects.filter(project => project.category === selectedCategory)
    : projects

  const getCategoryIcon = (category: string): LucideIcon => {
    const icons: Record<string, LucideIcon> = {
      web: Globe,
      backend: Server,
      devops: Cloud,
      mobile: Smartphone
    }
    return icons[category] || Code2
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      web: 'text-cyan-500',
      backend: 'text-violet-500',
      devops: 'text-amber-500',
      mobile: 'text-emerald-500'
    }
    return colors[category] || 'text-zinc-500'
  }

  return (
    <section className="section section-border">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-10 max-w-2xl"
        >
          <span className="section-label mb-4">Projects</span>
          <h2 className="text-balance mb-6">Selected work.</h2>
          <p className="text-lg text-zinc-500 dark:text-zinc-400">
            A mix of production systems, open-source contributions, and experiments in progress.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-10 flex flex-wrap gap-2"
        >
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id === 'all' ? null : category.id)}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
                (selectedCategory === category.id || (selectedCategory === null && category.id === 'all'))
                  ? 'bg-zinc-950 text-white shadow-sm dark:bg-zinc-50 dark:text-zinc-950'
                  : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-50'
              }`}
            >
              {category.name}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory || 'all'}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0, 0, 0.2, 1] }}
            className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3"
          >
            {filteredProjects.map((project, index) => {
              const CategoryIcon = getCategoryIcon(project.category)
              const isFeatured = project.featured

              return (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05, ease: [0, 0, 0.2, 1] }}
                  className={`group card card-hover flex flex-col ${isFeatured ? 'md:col-span-2 lg:col-span-2' : ''}`}
                >
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900 ${getCategoryColor(project.category)}`}>
                        <CategoryIcon size={16} strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                          {project.title}
                        </h3>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                          {project.category}
                        </span>
                      </div>
                    </div>
                    {project.featured && (
                      <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-600 dark:bg-cyan-950/30 dark:text-cyan-400">
                        Featured
                      </span>
                    )}
                  </div>

                  <p className="mb-6 flex-grow text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                    {project.description}
                  </p>

                  <div className="mb-5 flex flex-wrap gap-2">
                    {project.technologies.slice(0, isFeatured ? 6 : 4).map((tech) => (
                      <span key={tech} className="tag">
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > (isFeatured ? 6 : 4) && (
                      <span className="tag text-zinc-400">
                        +{project.technologies.length - (isFeatured ? 6 : 4)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 border-t border-zinc-100 pt-4 dark:border-zinc-800">
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 transition-colors duration-200 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-zinc-50"
                    >
                      <Github size={14} strokeWidth={1.5} />
                      Code
                    </a>
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 transition-colors duration-200 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-zinc-50"
                      >
                        <ExternalLink size={13} strokeWidth={1.5} />
                        Live
                      </a>
                    )}
                    <button
                      onClick={() => setSelectedProject(project.id)}
                      className="ml-auto flex items-center gap-1 text-xs font-semibold text-cyan-500 transition-colors duration-200 hover:text-cyan-600 dark:hover:text-cyan-400"
                    >
                      Details
                      <ArrowUpRight size={13} strokeWidth={2} />
                    </button>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-200 dark:border-zinc-800 dark:bg-zinc-800 md:grid-cols-4"
        >
          {[
            { label: 'Total Projects', value: projects.length.toString() },
            { label: 'Web Apps', value: projects.filter(p => p.category === 'web').length.toString() },
            { label: 'Backend APIs', value: projects.filter(p => p.category === 'backend').length.toString() },
            { label: 'Featured', value: projects.filter(p => p.featured).length.toString() }
          ].map((stat) => (
            <div key={stat.label} className="bg-canvas p-6 text-center dark:bg-canvas-dark">
              <div className="font-mono text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                {stat.value}
              </div>
              <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 8 }}
              transition={{ duration: 0.25, ease: [0, 0, 0.2, 1] }}
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-zinc-200 bg-white p-6 shadow-2xl dark:border-zinc-800 dark:bg-canvas-dark sm:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const project = projects.find(p => p.id === selectedProject)
                if (!project) return null
                const CategoryIcon = getCategoryIcon(project.category)

                return (
                  <>
                    <div className="mb-6 flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-900 ${getCategoryColor(project.category)}`}>
                          <CategoryIcon size={18} strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                            {project.title}
                          </h3>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                            {project.category}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedProject(null)}
                        className="rounded-lg p-1.5 text-zinc-400 transition-colors duration-200 hover:bg-zinc-100 hover:text-zinc-950 dark:hover:bg-zinc-900 dark:hover:text-zinc-50"
                        aria-label="Close modal"
                      >
                        <X size={18} strokeWidth={1.5} />
                      </button>
                    </div>

                    <p className="mb-6 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                      {project.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">
                        Technologies
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech) => (
                          <span key={tech} className="tag">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary flex-1 justify-center"
                      >
                        <Github size={15} strokeWidth={1.5} />
                        View Code
                      </a>
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary flex-1 justify-center"
                        >
                          <ExternalLink size={13} strokeWidth={1.5} />
                          Live Demo
                        </a>
                      )}
                    </div>
                  </>
                )
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default Projects
